/**
 * The channels a Nexoya Starter team may connect itself.
 *
 * Keyed off `Provider.name`, which is NOT a display name — it is a translation key
 * (`{provider.facebook}`), resolved at render by `translate(translations, name)`. Keying
 * off `provider_id` was the obvious choice and is not possible: ids are backend-owned and
 * most of these channels have no id anywhere in this repo. The seed array in
 * `ProvidersProvider` looks authoritative but is overwritten by the query result.
 *
 * Note "Meta" is `{provider.facebook}` and TikTok carries a `V1` suffix. Both are the kind
 * of mistake an allow-list written from the product names would make silently.
 *
 * This list says nothing about whether a channel can be connected via OAuth. That is a
 * runtime property of `Integration` — `type: 'auto'` plus a non-null `connectionUrl` —
 * because the `Provider` GraphQL type carries no such field, and the 28 `/oauth/<slug>`
 * routes are decorative (`OauthProviderFilters` reads only query params, never the path).
 */
export const APPROVED_CHANNEL_KEYS = [
  '{provider.google.ads}',
  '{provider.facebook}',
  '{provider.tiktokV1}',
  '{provider.pinterest}',
  '{provider.bing}',
  '{provider.google.analytics}',
  '{provider.google.analytics4}',
  '{provider.criteo}',
] as const;

/**
 * Amazon is approved but has no `{provider.amazon*}` translation key in this repo, and
 * ships as two products (`amazonSponsoredAds`, `amazonDsp` — see `paths.ts:85-86`).
 * Matching by prefix approves whichever key the backend actually returns.
 */
export const APPROVED_CHANNEL_KEY_PREFIXES = ['{provider.amazon'] as const;

/**
 * Approved channels that report on spend but never carry it.
 *
 * A SUBSET of `APPROVED_CHANNEL_KEYS`, asserted as such in `channels.test.ts` — these teams
 * genuinely have Google Analytics connected, they simply cannot buy media with it. The
 * distinction exists for one reason: the upsell dialog quantifies the allow-list to a buyer
 * as "N channels", and a buyer who counts two Analytics properties among them has caught the
 * product overstating itself. The copy says "channels"; only this count is ad-carrying.
 */
export const ANALYTICS_ONLY_CHANNEL_KEYS = ['{provider.google.analytics}', '{provider.google.analytics4}'] as const;

/**
 * How many approved channels can actually carry ad spend.
 *
 * Derived rather than authored, because this number is spoken out loud in sales copy —
 * `planAdditions.ts` and `scenarios.ts` interpolate it instead of writing "seven", so editing
 * the allow-list fails `channels.test.ts` rather than shipping a claim a buyer can disprove
 * from their own integrations page.
 *
 * Each entry in `APPROVED_CHANNEL_KEY_PREFIXES` counts once: a prefix is one channel family
 * that happens to ship as several products (Amazon is `amazonSponsoredAds` + `amazonDsp`),
 * and it is absent from `APPROVED_CHANNEL_KEYS` entirely, so it would otherwise count zero.
 */
export function approvedAdChannelCount(): number {
  return APPROVED_CHANNEL_KEYS.length - ANALYTICS_ONLY_CHANNEL_KEYS.length + APPROVED_CHANNEL_KEY_PREFIXES.length;
}

export function isApprovedChannel(providerName: string | null | undefined): boolean {
  if (!providerName) return false;
  if ((APPROVED_CHANNEL_KEYS as readonly string[]).includes(providerName)) return true;
  return APPROVED_CHANNEL_KEY_PREFIXES.some((prefix) => providerName.startsWith(prefix));
}
